// TODO: Refactored from ForwardMessageModal.tsx to consolidate duplicate color generation functions
export type ColorType = 'Grey' | 'Primary' | 'Red' | 'Green' | 'Orange'

// Function to generate a consistent color based on text
export const getColorFromText = (text?: string): ColorType => {
  if (!text) return 'Primary'

  // Available colors - prioritizing vibrant colors by repeating them
  const colors: ColorType[] = [
    'Primary',
    'Red',
    'Green',
    'Orange',
    'Primary',
    'Red',
    'Green',
    'Orange',
    'Grey',
  ]

  // Generate a hash from the text
  let hash = 0
  for (let i = 0; i < text.length; i++) {
    hash = text.charCodeAt(i) + ((hash << 5) - hash)
  }

  // Use the hash to pick a color
  const index = Math.abs(hash) % colors.length
  return colors[index]
}

// Function to determine if a color is light or dark
const isLightColor = (hsl: { h: number; s: number; l: number }): boolean => {
  // Colors with lightness > 60% are considered light
  return hsl.l > 60
}

// Function to generate a hex color from text
export const getHexColorFromText = (
  text?: string,
  options?: { darker?: boolean }
): { color: string; isLight: boolean } => {
  if (!text) return { color: '#7F8C8D', isLight: false } // Default to a neutral gray

  // Generate a hash from the text
  let hash = 0
  for (let i = 0; i < text.length; i++) {
    hash = text.charCodeAt(i) + ((hash << 5) - hash)
  }

  // More neutral colors with lower saturation and balanced lightness
  // Use a modulo with a smaller range to get more neutral hues
  // Neutral hues are often in the blue-green-gray spectrum (180-240)
  // or in the earthy tones (20-60)
  let h = Math.abs(hash) % 360 // Hue: 0-359

  // Adjust hue to favor more neutral colors
  // This creates a bias toward blues, greens, and earthy tones
  if (h > 60 && h < 180) {
    // Shift middle range hues toward blue-green
    h = 180 + (h % 60)
  } else if (h > 240 && h < 330) {
    // Shift purple-pink hues toward earthy tones
    h = 20 + (h % 40)
  }

  // Adjust saturation and lightness based on darker option
  let s: number
  let l: number

  if (options?.darker) {
    // Darker colors for white backgrounds
    s = 40 + (Math.abs(hash >> 8) % 25) // Saturation: 45-70% (more vibrant)
    l = 35 + (Math.abs(hash >> 16) % 15) // Lightness: 35-55% (darker)
  } else {
    // Original neutral colors
    s = 20 + (Math.abs(hash >> 8) % 25) // Saturation: 20-45% (more muted)
    l = 60 + (Math.abs(hash >> 16) % 15) // Lightness: 60-75% (more neutral)
  }

  const hslValues = { h, s, l }
  return {
    color: `hsl(${h}, ${s}%, ${l}%)`,
    isLight: isLightColor(hslValues),
  }
}

// Function to generate chip colors (background and text) from text
// Uses the same base color logic as getHexColorFromText but optimized for chips
export const getChipColorsFromText = (
  text?: string
): { backgroundColor: string; textColor: string } => {
  if (!text)
    return {
      backgroundColor: '#E6E3FF',
      textColor: '#8B7EFF', // Medium shade of the same purple color
    }

  // Use the same color generation logic as getHexColorFromText for consistency
  const baseColor = getHexColorFromText(text)

  // Extract HSL values from the base color
  const hslMatch = baseColor.color.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/)
  if (!hslMatch) {
    // Fallback if regex fails
    return {
      backgroundColor: baseColor.color,
      textColor: baseColor.isLight ? '#374151' : '#F9FAFB',
    }
  }

  const h = parseInt(hslMatch[1])
  const s = parseInt(hslMatch[2])
  const l = parseInt(hslMatch[3])

  // Create chip-optimized colors based on the avatar's base color
  const chipBackgroundL = Math.min(l + 15, 85) // Lighter background (75-85%)
  const chipBackgroundS = Math.min(s + 20, 60) // More saturated background (40-60%)
  const chipTextL = Math.max(l - 25, 30) // Darker text (30-50%)
  const chipTextS = Math.min(s + 30, 80) // More saturated text (50-80%)

  return {
    backgroundColor: `hsl(${h}, ${chipBackgroundS}%, ${chipBackgroundL}%)`,
    textColor: `hsl(${h}, ${chipTextS}%, ${chipTextL}%)`,
  }
}
